import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('ss_token');
      if (!token) {
        setLoading(false);
        return;
      }
      const res = await api.get('/api/auth/me');
      setUser(res.data);
      // V14.5 Part 17 — sync browser timezone (idempotent, fire-and-forget).
      // Backend uses this to format outbound WATI/email booking
      // confirmations in the learner's local TZ instead of raw IST.
      try {
        const tz = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone;
        if (tz && tz !== res.data?.timezone) {
          api.put('/api/users/me/timezone', { timezone: tz }).catch(() => { });
        }
      } catch { /* noop */ }
    } catch (err) {
      localStorage.removeItem('ss_token');
      localStorage.removeItem('ss_user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const res = await api.post('/api/auth/login', { email, password });
    localStorage.setItem('ss_token', res.data.access_token);
    localStorage.setItem('ss_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    // Best-effort: tell the server this token is dead, then clear locally right away.
    // Capture the token now: axios request interceptors run async, after the removeItem below.
    const tok = localStorage.getItem('ss_token');
    if (tok) api.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${tok}` } }).catch(() => {});
    localStorage.removeItem('ss_token');
    localStorage.removeItem('ss_user');
    setUser(null);
  };

  const register = async (data) => {
    const res = await api.post('/api/auth/register', data);
    localStorage.setItem('ss_token', res.data.access_token);
    localStorage.setItem('ss_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const setAuth = useCallback(({ token, user: u }) => {
    // V13.14 — direct-set (used by SSO magic-link exchange)
    localStorage.setItem('ss_token', token);
    localStorage.setItem('ss_user', JSON.stringify(u));
    setUser(u);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, register, setAuth, loading, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
